import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap } from 'rxjs/operators';
import { AiAssessmentsService, WorkspaceService } from '@pt/api-client';
import {
  AiAssessmentDocCreateRequest,
  AiAssessmentDocUpdateRequest,
  DocumentDuplicateResponse,
  WorkspaceItem,
  WorkspaceItemType,
  WorkspaceMoveRequest
} from '@primatoo-ms-organization';
import { of } from 'rxjs';
import { Store } from '@ngrx/store';

import * as AiAssessmentActions from '../actions/ai-assessment.actions';

import { WidgetDialogService } from '../../services/widget-dialog.service';
import { PtCoreActions } from '@pt/pt-core';
import { DialogService } from '../../../services';
import { ValidateDocumentRequest } from 'it/primatoo/openapi/api/org/schemas/common/docs/ValidateDocumentRequest';
import { CreateDocResponse } from 'it/primatoo/openapi/api/org/schemas/common/docs/CreateDocResponse';
import { UiDocumentItem } from '@pt/pt-models';
import { UpdateDocResponse } from 'it/primatoo/openapi/api/org/schemas/common/docs/UpdateDocResponse';

@Injectable()
export class AiAssessmentEffects {

  publishAiAssessment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AiAssessmentActions.publishAiAssessment),
      switchMap((action) =>
        this.aiAssessmentService.publish(action.docId).pipe(
          switchMap(() => {
            if (action?.skipBack) {
              return [
                AiAssessmentActions.publishAiAssessmentSuccess(),
                PtCoreActions.showOperationSuccessNotification()
              ];
            } else {
              return [
                AiAssessmentActions.publishAiAssessmentSuccess(),
                PtCoreActions.navigateBack({
                  hop: -1,
                  queryParams: {
                    opened: true,
                    id: action.docId
                  }
                }),
                PtCoreActions.showOperationSuccessNotification()
              ];
            }
          }),
          catchError(err =>
            of(AiAssessmentActions.publishAiAssessmentFailed({error: err.message}))
          )
        )
      )
    );
  });
  publishAiAssessmentWithConfirm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AiAssessmentActions.publishAiAssessmentWithConfirm),
      switchMap((action: { docId: number, skipBack?: boolean, hideInfo?: boolean }) => {
        const title = 'TITLE.AI_ASSESSMENT_PUBLISH';
        const message = 'MESSAGE.AI_ASSESSMENT_PUBLISH';
        // const info = 'MESSAGE.ON_END_OPERATION_COME_BACK';

        return this.widgetDialogService.runConfirmDialog(
          title,
          message,
          // !action.hideInfo ? info : null
        ).pipe(
          map((response) => {
            if (response) {
              this.store.dispatch(AiAssessmentActions.publishAiAssessment({
                docId: action.docId,
                skipBack: action?.skipBack
              }));
            }
          })
        );
      })
    ), {dispatch: false}
  );
  validateDocument$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AiAssessmentActions.validateAiAssessment),
      switchMap((action: { document: UiDocumentItem, request: ValidateDocumentRequest, skipBack?: boolean }) =>
        this.aiAssessmentService.validate(action.document.docId, action.request).pipe(
          switchMap(() => {
            if (action?.skipBack) {
              return [
                AiAssessmentActions.validateAiAssessmentSuccess(),
                PtCoreActions.showOperationSuccessNotification()
              ];
            } else {
              return [
                AiAssessmentActions.validateAiAssessmentSuccess(),
                PtCoreActions.navigateBack({
                  hop: -1,
                  queryParams: {
                    opened: true,
                    id: action.document.docId
                  }
                }),
                PtCoreActions.showOperationSuccessNotification()
              ];
            }
          }),
          catchError(err =>
            of(AiAssessmentActions.validateAiAssessmentFailed({error: err.message}))
          )
        )
      )
    );
  });
  validateDocumentWithConfirm$ = createEffect(() => {
      return this.actions$.pipe(
        ofType(AiAssessmentActions.validateAiAssessmentWithConfirm),
        map((action: { document: UiDocumentItem, skipBack?: boolean, hideInfo?: boolean }) => {
          const title = 'TITLE.AI_ASSESSMENT_VALIDATE';
          const message = 'MESSAGE.AI_ASSESSMENT_VALIDATE';
          return this.dialogService.runValidateRequestDialog(title, message).subscribe((request) => {
            if (request) {
              this.store.dispatch(AiAssessmentActions.validateAiAssessment({
                document: action?.document,
                request,
                skipBack: action.skipBack
              }));
            } else {
              this.store.dispatch(AiAssessmentActions.validateAiAssessmentFailed({ error: null }))
            }
          });
        })
      );
    }, {dispatch: false}
  );
  reopenAiAssessment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AiAssessmentActions.reopenAiAssessment),
      switchMap((action) =>
        this.aiAssessmentService.reopen(action.id).pipe(
          switchMap(() => [
              AiAssessmentActions.reopenAiAssessmentSuccess({id: action.id}),
              PtCoreActions.showOperationSuccessNotification()
            ]
          ),
          catchError(err =>
            of(AiAssessmentActions.reopenAiAssessmentFailed({error: err.message}))
          )
        )
      )
    );
  });
  reopenAiAssessmentWithConfirm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AiAssessmentActions.reopenAiAssessmentWithConfirm),
      switchMap((action) => {
        const title = 'TITLE.AI_ASSESSMENT_REOPEN';
        const message = 'MESSAGE.AI_ASSESSMENT_REOPEN';

        return this.widgetDialogService.runConfirmDialog(title, message).pipe(
          map((response) => {
            if (response) {
              this.store.dispatch(AiAssessmentActions.reopenAiAssessment({id: action?.id}));
            }
          })
        );
      })
    ), {dispatch: false}
  );
  updateAiAssessment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AiAssessmentActions.updateAiAssessment),
      switchMap((action: { id: number, request: AiAssessmentDocUpdateRequest }) =>
        this.aiAssessmentService.update(action.id, action.request).pipe(
          map((response: UpdateDocResponse) =>
            AiAssessmentActions.updateAiAssessmentSuccess({response})
          ),
          catchError(err =>
            of(AiAssessmentActions.updateAiAssessmentFailed({error: err.message}))
          )
        )
      )
    );
  });
  renameAiAssessment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AiAssessmentActions.renameAiAssessment),
      switchMap((action) =>
        this.aiAssessmentService.rename(action.id, action.request).pipe(
          map(() =>
            AiAssessmentActions.renameAiAssessmentSuccess({id: action.id})
          ),
          catchError(err =>
            of(AiAssessmentActions.renameAiAssessmentFailed({error: err.message}))
          )
        )
      )
    );
  });
  createAiAssessment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AiAssessmentActions.createAiAssessment),
      switchMap((action: { request: AiAssessmentDocCreateRequest }) =>
        this.aiAssessmentService.create(action.request).pipe(
          map((response: CreateDocResponse) =>
            AiAssessmentActions.createAiAssessmentSuccess({id: response.docId})
          ),
          catchError(err =>
            of(AiAssessmentActions.createAiAssessmentFailed({error: err.message}))
          )
        )
      )
    );
  });

  // updateAiAssessmentSuccess$ = createEffect(() => {
  //   return this.actions$.pipe(
  //     ofType(AiAssessmentActions.updateAiAssessmentSuccess),
  //     switchMap((action: { response: AiAssessmentDocUpdateResponse }) =>
  //       this.aiAssessmentService.getTemplate(action.response.docId).pipe(
  //         map((response: TemplateWithUser) =>
  //           NgrxWidgetActions.updateStoreTemplate({
  //             template: response as AiAssessmentEditable,
  //             docType: DocumentEditorType.AI_ASSESSMENT
  //           })
  //         ),
  //         catchError(() =>
  //           of(NgrxWidgetActions.updateStoreTemplate({template: null, docType: null}))
  //         )
  //       )
  //     )
  //   );
  // });
  moveAiAssessment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AiAssessmentActions.moveAiAssessment),
      switchMap((action: { id: number, workspaceMoveRequest: WorkspaceMoveRequest }) =>
        this.workspaceService.move(action.id, WorkspaceItemType.AI_ASSESSMENT, action.workspaceMoveRequest).pipe(
          map((item: WorkspaceItem) =>
            AiAssessmentActions.moveAiAssessmentSuccess({item})
          ),
          catchError(err => of(
            AiAssessmentActions.moveAiAssessmentFailed({error: err.message})
          ))
        )
      )
    );
  });
  duplicateAiAssessment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AiAssessmentActions.duplicateAiAssessment),
      switchMap((action) =>
        this.aiAssessmentService.duplicate(action.id, action.request).pipe(
          map((response: DocumentDuplicateResponse) =>
            AiAssessmentActions.duplicateAiAssessmentSuccess({
              response
            })
          ),
          catchError(err => of(
            AiAssessmentActions.duplicateAiAssessmentFailed({
              error: err?.message
            })
          ))
        )
      )
    )
  );
  deleteAiAssessment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AiAssessmentActions.deleteAiAssessment),
      switchMap((action: { id: number }) =>
        this.aiAssessmentService.delete(action.id).pipe(
          map(() => AiAssessmentActions.deleteAiAssessmentSuccess()),
          catchError(err => of(
            AiAssessmentActions.deleteAiAssessmentFailed({
              error: err.message
            })
          ))
        )
      ));
  });

  constructor(
    private store: Store,
    private actions$: Actions,
    private widgetDialogService: WidgetDialogService,
    private aiAssessmentService: AiAssessmentsService,
    private workspaceService: WorkspaceService,
    private dialogService: DialogService
  ) {
  }

  // deleteAiAssessmentWithVersion$ = createEffect(() =>
  //   this.actions$.pipe(
  //     ofType(AiAssessmentActions.deleteAiAssessmentWithVersion),
  //     switchMap((action: { id: number, version: number }) => {
  //         const title = this.translateService.stream('TITLE.AI_ASSESSMENT_DELETE');
  //         const message = this.translateService.stream('MESSAGE.AI_ASSESSMENT_DELETE');
  //         return this.dialogService.runConfirmDialogWithValidation({
  //           title,
  //           message,
  //           dialogType: DialogType.DELETE,
  //           size: 'md'
  //         }).pipe(
  //           map((response) => {
  //             if (response) {
  //               this.aiAssessmentService.delete(action.id).subscribe(
  //                 (success) => {
  //                   this.store.dispatch(AiAssessmentActions.deleteAiAssessmentWithVersionSuccess());
  //                   this.store.dispatch(PtCoreActions.navigateBack({
  //                     hop: -1,
  //                     queryParams: {
  //                       opened: true,
  //                       id: action.id
  //                     }
  //                   }));
  //                   this.store.dispatch(PtCoreActions.showOperationSuccessNotification());
  //                 },
  //                 (error) => AiAssessmentActions.deleteAiAssessmentWithVersionFailed({
  //                   error: error.message
  //                 })
  //               )
  //             }
  //           })
  //         );
  //       }
  //     )
  //   ), {dispatch: false}
  // );

}
